import { Eye } from 'react-feather';
import { useModal } from 'react-modal-hook';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { LinkBlockContainer } from '../../../../common/UI/Nav';
import DateRange from '../../../../common/Common/DateRange';
import UI from '../../../../common/UI';

interface UsePickDashboardModalProps {
  participant?: Participant
}

interface Participant {
  registration_summaries: RegistrationSummary[];
}

interface RegistrationSummary {
  id: string;
  dashboard_url: string;
  event: {
    title: string;
    start_date: string;
    end_date: string;
  }
}

const usePickDashboardModal = ({ participant }: UsePickDashboardModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal} maxWidth={600}>
        <DashboardPicker participant={participant} />
      </UI.Modal>
    ),
    [participant],
  );

  return [openModal, closeModal];
};

interface DashboardPickerProps {
  participant: Participant;
  limit?: number;
}

const DashboardPicker = ({ participant, limit = 5 }: DashboardPickerProps) => {
  const { t } = useTranslation();

  const [showingAll, setShowingAll] = useState(false);
  const summaries = participant.registration_summaries.slice(0, !showingAll ? limit : undefined);

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.H4 sc={{ mb: 2 }}>
          {t('open_dashboard')}
        </UI.H4>

        <UI.Paragraph sc={{ mb: 0 }}>
          {t('open_dashboard_description')}
        </UI.Paragraph>
      </UI.Div>

      <LinksContainer>
        {summaries.map((summary) => (
          <UI.LinkBlock
            href={summary.dashboard_url}
            target="_blank"
            rel="noopener noreferrer"
            title={summary.event.title}
            key={summary.id}
          >
            <UI.GridContainer sc={{ columns: '1fr 18px', gutter: 0.5, alignVertical: 'center' }}>
              <UI.Div>
                <UI.Div sc={{ strong: true }}>
                  {summary.event.title}
                </UI.Div>
                <UI.Span sc={{ color: 'gray.500', small: true }}>
                  <DateRange start={summary.event.start_date} end={summary.event.end_date} />
                </UI.Span>
              </UI.Div>
              <UI.Icon>
                <Eye />
              </UI.Icon>
            </UI.GridContainer>
          </UI.LinkBlock>
        ))}
      </LinksContainer>
      {participant.registration_summaries.length > limit && (
        <UI.A onClick={() => setShowingAll((showingAll) => !showingAll)}>
          {!showingAll ? t('show_more') : t('show_less')}
        </UI.A>
      )}
    </UI.FormGrid>
  );
};

const LinksContainer = styled(UI.Div)`
  ${({ theme }) => css`
    > * + * {
      margin-top: ${theme.gutter / 4}px;
    }

    ${LinkBlockContainer} {
      padding: ${theme.gutter / 3}px ${theme.gutter / 2}px;
      border-radius: ${theme.borderRadiuses.md}px;
      border: 1px solid ${theme.colors.gray[200]};

      &:hover {
        background: ${theme.colors.gray[50]};
      }
    }
  `}
`;

export default usePickDashboardModal;
